import { useCallback, useEffect, useState } from 'react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { Fingerprint, KeyRound, Plus, Trash2 } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Input, Label } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import { passkeyApi, type PasskeyItem } from '@/services/api';

/**
 * Page Sécurité du compte — gestion des passkeys WebAuthn / FIDO2.
 *
 * Accessible à tous les utilisateurs authentifiés. Permet :
 *   - d'enregistrer une nouvelle passkey (téléphone, ordinateur, Yubikey…)
 *   - de lister les passkeys enregistrées
 *   - de révoquer une passkey perdue ou compromise
 *
 * Le TOTP existant reste actif en parallèle. La passkey est une
 * alternative au TOTP pour le login, qui résiste au phishing AiTM.
 *
 * Cf SECURITY.md §25.
 *
 * NB : la logique métier (useConfirm, useToast) est dans `PasskeysSection`
 * qui est rendu DANS `<AppLayout>` — sinon `useConfirm()` est appelé avant
 * que le `ConfirmProvider` (situé dans AppLayout) ne soit monté → throw
 * "doit être utilisé dans un <ConfirmProvider>".
 */
export function SecurityPage() {
  return (
    <AppLayout>
      <PageHeader
        title="Sécurité du compte"
        subtitle="Gérez vos méthodes de connexion fortes (passkeys)."
      />
      <PasskeysSection />
    </AppLayout>
  );
}

function PasskeysSection() {
  const accessToken = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<PasskeyItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState('');

  const load = useCallback(async () => {
    try {
      setItems(await passkeyApi.list(accessToken));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRegister() {
    if (!deviceLabel.trim()) {
      toast({ tone: 'danger', message: 'Donnez un nom à votre passkey (ex: iPhone perso).' });
      return;
    }
    setRegistering(true);
    try {
      // Étape 1 : challenge serveur
      const options = await passkeyApi.registerOptions(accessToken);
      // Étape 2 : invocation native navigator.credentials.create
      // Le navigateur prompt empreinte/Face ID/PIN selon le device.
      const response = await startRegistration({
        optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      // Étape 3 : envoi de la response signée + label
      await passkeyApi.registerVerify(accessToken, response, deviceLabel.trim());
      toast({
        tone: 'success',
        title: 'Passkey enregistrée',
        message: `Vous pouvez maintenant vous connecter avec ${deviceLabel.trim()}.`,
      });
      setDeviceLabel('');
      await load();
    } catch (err) {
      // Distinguer les erreurs courantes pour donner un message utile.
      const msg = err instanceof Error ? err.message : 'Erreur';
      if (msg.includes('NotAllowedError') || msg.includes('cancelled')) {
        toast({ tone: 'warning', message: 'Enregistrement annulé.' });
      } else if (msg.includes('NotSupportedError')) {
        toast({
          tone: 'danger',
          message:
            'Cet appareil ne supporte pas WebAuthn. Utilisez un téléphone récent ou un Yubikey.',
        });
      } else {
        toast({ tone: 'danger', title: 'Échec enregistrement', message: msg });
      }
    } finally {
      setRegistering(false);
    }
  }

  async function handleDelete(p: PasskeyItem) {
    const ok = await confirm({
      title: `Révoquer la passkey "${p.deviceLabel}" ?`,
      message:
        "Cette passkey ne pourra plus être utilisée pour se connecter. Si vous l'avez perdue, c'est la bonne action.",
      variant: 'danger',
      confirmLabel: 'Révoquer',
    });
    if (!ok) return;
    try {
      await passkeyApi.delete(accessToken, p.id);
      toast({ tone: 'success', message: 'Passkey révoquée.' });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  return (
    <>
      {error && <Banner tone="danger">{error}</Banner>}

      <Card>
        <CardTitle>Passkeys (WebAuthn / FIDO2)</CardTitle>
        <p className="mt-1 text-sm text-text-muted">
          Les passkeys remplacent le code à 6 chiffres du téléphone par une authentification
          cryptographique liée à ce site. Elles
          <strong> résistent au phishing</strong> : une fausse copie de ce site ne peut pas obtenir
          une signature valide. Vous pouvez utiliser votre empreinte de smartphone, Touch ID,
          Windows Hello, ou une clé physique (Yubikey).
        </p>

        {/* Form d'ajout */}
        <div className="mt-5 flex items-end gap-3 border-t border-border-subtle pt-4">
          <div className="flex-1">
            <Label htmlFor="passkey-label">Nom de la passkey</Label>
            <Input
              id="passkey-label"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              placeholder="iPhone perso, MacBook, Yubikey jaune…"
              disabled={registering}
              maxLength={100}
            />
          </div>
          <Button
            variant="primary"
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={handleRegister}
            loading={registering}
            disabled={!deviceLabel.trim()}
          >
            Enregistrer
          </Button>
        </div>

        {/* Liste */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-medium text-brand">Passkeys enregistrées</h3>
          {items === null ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Fingerprint className="h-8 w-8 text-text-muted" />}
              title="Aucune passkey"
              description="Enregistrez votre premier appareil ci-dessus pour activer la connexion par passkey."
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <KeyRound className="h-4 w-4 text-accent shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-brand">{p.deviceLabel}</div>
                      <div className="text-[11px] text-text-muted">
                        Enregistrée le{' '}
                        {new Date(p.createdAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                        {p.lastUsedAt && (
                          <>
                            {' · dernière utilisation '}
                            {new Date(p.lastUsedAt).toLocaleDateString('fr-FR')}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                    onClick={() => void handleDelete(p)}
                  >
                    Révoquer
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </>
  );
}

/**
 * Helper exporté pour la page Login : déclenche le flow de connexion par
 * passkey. Retourne `true` si la session a été créée, `false` si l'user
 * a annulé. En cas d'erreur réelle, throw.
 */
export async function loginWithPasskey(): Promise<{
  accessToken: string;
  user: { id: string; email: string; role: string };
} | null> {
  const { options, challengeId } = await passkeyApi.loginOptions();
  let response;
  try {
    response = await startAuthentication({
      optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      return null; // user a annulé
    }
    throw err;
  }
  return passkeyApi.loginVerify(challengeId, response) as Promise<{
    accessToken: string;
    user: { id: string; email: string; role: string };
  }>;
}
